
import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { POAssignmentApi, POCompletedInspectionApi, PurchaseOrderApi } from '../types';
import PeopleHoverCard, { Avatar, PersonEntry, PersonRow } from '../components/PeopleHoverCard';
import { getAssignedByPeople, getAssignedToPeople } from '../utils/poPeople';

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

type ViewMode = 'day' | 'week' | 'month';
// Due-date colour of open POs; completed ones ('done') turn grey.
type DueStatus = 'green' | 'orange' | 'red' | 'done';
type EventStatus = 'Scheduled' | 'In Progress' | 'Passed' | 'Failed';

interface CalendarEvent {
  key: string;
  date: string; // YYYY-MM-DD
  po: PurchaseOrderApi;
  // This day's slice of the PO: open assignments due that day and inspections completed for it
  assignments: POAssignmentApi[];
  completed: POCompletedInspectionApi[];
  inspectionTypes: string[];
  assignedTo: PersonEntry[];
  assignedBy: PersonEntry[];
  inspection: EventStatus;
  status: DueStatus;
  // False for inspections completed before due dates were kept — they sit on their completion day
  dueKnown: boolean;
}

const STATUS_ORDER: Record<DueStatus, number> = { red: 0, orange: 1, green: 2, done: 3 };
const STATUS_LABELS: Record<DueStatus, string> = { red: 'Overdue', orange: 'Due Soon', green: 'On Track', done: 'Completed' };
const STATUS_STYLES: Record<DueStatus, { dot: string; pill: string; text: string }> = {
  green: { dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', text: 'text-emerald-600' },
  orange: { dot: 'bg-amber-500', pill: 'bg-amber-50 text-amber-700 border-amber-200', text: 'text-amber-600' },
  red: { dot: 'bg-red-500', pill: 'bg-red-50 text-red-700 border-red-200', text: 'text-red-600' },
  done: { dot: 'bg-slate-400', pill: 'bg-slate-100 text-slate-500 border-slate-200', text: 'text-slate-500' },
};

const EVENT_STATUSES: EventStatus[] = ['Scheduled', 'In Progress', 'Passed', 'Failed'];

// Same colours as the PO list's inspection chips
const INSPECTION_STYLES: Record<string, string> = {
  Passed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'In Progress': 'bg-amber-50 text-amber-700 border-amber-200',
  Scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  Failed: 'bg-red-50 text-red-700 border-red-200',
  Pending: 'bg-slate-50 text-slate-500 border-slate-200',
};

const INSPECTION_TYPES: [string, string][] = [
  ['pilot', 'Pilot'], ['fabric', 'Fabric'], ['line', 'Line'], ['final', 'Final'], ['container', 'Container'],
];

const DUE_SOON_WINDOW_DAYS = 5;

// The side panel docks beside the calendar from here up; below it slides over as a drawer
const WIDE_QUERY = '(min-width: 1280px)';

const toDateKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const daysUntil = (dateKey: string) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${dateKey}T00:00:00`);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
};

const computeStatus = (dateKey: string): DueStatus => {
  const diffDays = daysUntil(dateKey);
  if (diffDays < 0) return 'red';
  if (diffDays <= DUE_SOON_WINDOW_DAYS) return 'orange';
  return 'green';
};

const fmtLongDate = (dateKey: string) =>
  new Date(`${dateKey}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });

const fmtDateTime = (iso: string) => {
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} at ${time}`;
};

const dueText = (ev: CalendarEvent) => {
  if (ev.status === 'done') return ev.dueKnown ? 'Due date' : 'Completed on (no due date recorded)';
  const days = daysUntil(ev.date);
  if (days === 0) return 'Due today';
  const n = Math.abs(days);
  const unit = n === 1 ? 'day' : 'days';
  return days > 0 ? `Due in ${n} ${unit}` : `Overdue by ${n} ${unit}`;
};

const getWeekDates = (date: Date): Date[] => {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay());
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

const getMonthGridDates = (date: Date): Date[] => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const start = new Date(firstOfMonth);
  start.setDate(start.getDate() - firstOfMonth.getDay());
  const lastOfMonth = new Date(year, month + 1, 0);
  const end = new Date(lastOfMonth);
  end.setDate(end.getDate() + (6 - lastOfMonth.getDay()));

  const dates: Date[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const useIsWide = () => {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE_QUERY).matches);
  useEffect(() => {
    const mql = window.matchMedia(WIDE_QUERY);
    const onChange = () => setWide(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return wide;
};

const ICON_SIZES = {
  xs: { box: 'h-2.5 w-2.5', glyph: 'text-[7px]', ring: 'border' },
  sm: { box: 'h-3 w-3', glyph: 'text-[9px]', ring: 'border-[1.5px]' },
  md: { box: 'h-4 w-4', glyph: 'text-[11px]', ring: 'border-[1.5px]' },
  lg: { box: 'h-10 w-10', glyph: 'text-[24px]', ring: 'border-[3px]' },
};

// Reminder-style circle: ○ scheduled / pending and ◐ in progress take the surrounding
// text colour; ✓ passed and ✕ failed are always green and red.
const StatusIcon: React.FC<{ status: string; size?: keyof typeof ICON_SIZES }> = ({ status, size = 'md' }) => {
  const { box, glyph, ring } = ICON_SIZES[size];
  const done = status === 'Passed' || status === 'Completed';
  if (done || status === 'Failed') {
    return (
      <span className={`${box} inline-flex shrink-0 items-center justify-center rounded-full text-white ${done ? 'bg-emerald-500' : 'bg-red-500'}`}>
        <span className={`material-symbols-outlined font-bold leading-none ${glyph}`}>{done ? 'check' : 'close'}</span>
      </span>
    );
  }
  return (
    <span
      className={`${box} ${ring} inline-block shrink-0 rounded-full border-current`}
      style={status === 'In Progress' ? { background: 'linear-gradient(90deg, currentColor 50%, transparent 50%)' } : undefined}
    />
  );
};

const InspectionPill: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${INSPECTION_STYLES[status] || INSPECTION_STYLES.Pending}`}>
    <StatusIcon status={status} size="sm" />
    {status}
  </span>
);

const TypeChips: React.FC<{ types: string[] }> = ({ types }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {types.map(t => (
      <span key={t} className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold capitalize text-slate-600">
        {t}
      </span>
    ))}
  </div>
);

const EventChip: React.FC<{ ev: CalendarEvent; size: 'month' | 'week'; selected: boolean; onClick: () => void }> = ({ ev, size, selected, onClick }) => {
  const first = ev.assignedTo[0];
  const avatarSize = size === 'month' ? 'h-4 w-4 text-[7px]' : 'h-5 w-5 text-[8px]';
  const avatar = first ? (
    <Avatar person={first} size={avatarSize} tick={false} />
  ) : (
    <span className={`${avatarSize} flex shrink-0 items-center justify-center rounded-full bg-white/80 text-slate-400`}>
      <span className="material-symbols-outlined text-[11px]">person</span>
    </span>
  );
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${ev.po.po_number} — ${ev.po.customer_name} · ${ev.inspection}`}
      className={`flex w-full min-w-0 items-center justify-center rounded-md border px-0.5 py-0.5 text-left font-semibold transition-shadow hover:shadow-sm sm:justify-start sm:px-1.5 ${
        size === 'week' ? 'text-[11px] sm:py-1' : 'text-[10px]'
      } ${STATUS_STYLES[ev.status].pill} ${selected ? 'ring-2 ring-[#136bce]' : ''}`}
    >
      {/* Phones: cells are too narrow for text, so the status sits on the avatar */}
      <span className="relative sm:hidden">
        {avatar}
        <span className="absolute -bottom-1 -right-1 flex rounded-full bg-white p-px">
          <StatusIcon status={ev.inspection} size="xs" />
        </span>
      </span>
      <span className="hidden min-w-0 flex-1 items-center gap-1 sm:flex">
        {avatar}
        <span className="min-w-0 flex-1 truncate">{ev.po.po_number}</span>
        <StatusIcon status={ev.inspection} size="sm" />
      </span>
    </button>
  );
};

const DayEventCard: React.FC<{ ev: CalendarEvent; selected: boolean; onClick: () => void }> = ({ ev, selected, onClick }) => {
  const styles = STATUS_STYLES[ev.status];
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className={`flex items-start gap-4 rounded-xl border bg-white p-4 hover:shadow-md transition-all cursor-pointer ${
        selected ? 'border-[#136bce] ring-2 ring-[#136bce]/20' : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <div className={`mt-1.5 h-3 w-3 flex-shrink-0 rounded-full ${styles.dot}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-semibold text-[#136bce]">{ev.po.po_number}</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {ev.status !== 'done' && (
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${styles.pill}`}>
                {STATUS_LABELS[ev.status]}
              </span>
            )}
            <InspectionPill status={ev.inspection} />
          </div>
        </div>
        <div className="mt-0.5 text-sm font-medium text-slate-900">{ev.po.customer_name}</div>
        {ev.inspectionTypes.length > 0 && (
          <div className="mt-2">
            <TypeChips types={ev.inspectionTypes} />
          </div>
        )}
        <div className="mt-3 flex items-center">
          <PeopleHoverCard title="Assigned To" people={ev.assignedTo} emptyLabel="Unassigned" showTicks />
        </div>
      </div>
    </div>
  );
};

const PanelSection: React.FC<{ title: string; count?: number; children: React.ReactNode }> = ({ title, count, children }) => (
  <section className="rounded-2xl bg-white p-2 ring-1 ring-slate-200">
    <div className="flex items-center justify-between px-2.5 pb-1 pt-1.5">
      <p className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">{title}</p>
      {count !== undefined && count > 0 && (
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">{count}</span>
      )}
    </div>
    {children}
  </section>
);

const HERO_STYLES: Record<EventStatus, string> = {
  Scheduled: 'bg-blue-50 text-blue-700 ring-blue-200',
  'In Progress': 'bg-amber-50 text-amber-700 ring-amber-200',
  Passed: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  Failed: 'bg-red-50 text-red-700 ring-red-200',
};

const HERO_NOTES: Record<EventStatus, string> = {
  Scheduled: 'Assigned — the inspector hasn’t started yet.',
  'In Progress': 'The inspector has started this inspection.',
  Passed: '',
  Failed: '',
};

const EventPanel: React.FC<{ ev: CalendarEvent; onClose: () => void }> = ({ ev, onClose }) => {
  const navigate = useNavigate();
  const lastCompleted = ev.completed[0];
  const statuses = ev.po.inspection_statuses || {};
  const finished = ev.inspection === 'Passed' || ev.inspection === 'Failed';

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-start gap-3 border-b border-slate-200 bg-white px-5 py-4">
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">Purchase Order</p>
          <Link
            to={`/purchase-orders/${ev.po.id}`}
            className="group mt-0.5 inline-flex max-w-full items-center gap-1 text-xl font-bold text-[#136bce] hover:underline"
          >
            <span className="truncate">{ev.po.po_number}</span>
            <span className="material-symbols-outlined text-[18px] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5">arrow_outward</span>
          </Link>
          <p className="truncate text-sm font-medium text-slate-700">{ev.po.customer_name}</p>
          {ev.po.customer_location && <p className="truncate text-xs text-slate-500">{ev.po.customer_location}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
        >
          <span className="material-symbols-outlined text-[18px]">close</span>
        </button>
      </div>

      <div className="custom-scrollbar flex-1 space-y-3 overflow-y-auto p-4 sm:p-5">
        {/* Inspection status */}
        <div className={`rounded-2xl p-4 ring-1 ${HERO_STYLES[ev.inspection]}`}>
          <div className="flex items-center gap-3">
            <StatusIcon status={ev.inspection} size="lg" />
            <div className="min-w-0">
              <p className="text-[9px] font-black uppercase tracking-[0.14em] opacity-70">Final Inspection</p>
              <p className="text-lg font-bold leading-tight">{ev.inspection}</p>
            </div>
          </div>
          <p className="mt-3 text-xs font-medium opacity-80">
            {finished && lastCompleted ? `Completed: ${fmtDateTime(lastCompleted.completed_at)}` : HERO_NOTES[ev.inspection]}
          </p>
        </div>

        {/* Due date */}
        <div className="rounded-2xl bg-white p-4 ring-1 ring-slate-200">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
              <span className="material-symbols-outlined text-[18px]">event</span>
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800">{fmtLongDate(ev.date)}</p>
              <p className={`text-xs font-medium ${STATUS_STYLES[ev.status].text}`}>{dueText(ev)}</p>
            </div>
          </div>
          {ev.inspectionTypes.length > 0 && (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <TypeChips types={ev.inspectionTypes} />
            </div>
          )}
        </div>

        <PanelSection title="Assigned To" count={ev.assignedTo.length}>
          {ev.assignedTo.length > 0 ? (
            ev.assignedTo.map(p => <PersonRow key={p.key} person={p} tick={p.status === 'completed'} />)
          ) : (
            <p className="px-2.5 pb-2 text-sm italic text-slate-400">Unassigned</p>
          )}
        </PanelSection>

        <PanelSection title="Assigned By" count={ev.assignedBy.length}>
          {ev.assignedBy.length > 0 ? (
            ev.assignedBy.map(p => <PersonRow key={p.key} person={p} tick={false} />)
          ) : (
            <p className="px-2.5 pb-2 text-sm italic text-slate-400">-</p>
          )}
        </PanelSection>

        <PanelSection title="All Inspections on this PO">
          <div className="divide-y divide-slate-100">
            {INSPECTION_TYPES.map(([key, label]) => (
              <div key={key} className="flex items-center justify-between gap-3 px-2.5 py-2">
                <span className="text-sm text-slate-600">{label}</span>
                <InspectionPill status={statuses[key] || 'Pending'} />
              </div>
            ))}
          </div>
        </PanelSection>
      </div>

      <div className="border-t border-slate-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <button
          type="button"
          onClick={() => navigate(`/purchase-orders/${ev.po.id}`)}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#136bce] px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
        >
          Open PO Details
          <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
        </button>
      </div>
    </div>
  );
};

const POCalendar: React.FC = () => {
  const navigate = useNavigate();
  const isWide = useIsWide();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ViewMode>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    const fetchAssignedPOs = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/api/v1/purchase-orders?limit=1000`, { headers: getHeaders() });
        const data = await response.json();
        if (data.success) setPurchaseOrders(data.data);
      } catch (err) {
        console.error('Failed to fetch purchase orders for calendar:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAssignedPOs();
  }, []);

  useEffect(() => {
    if (!selectedKey) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelectedKey(null); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedKey]);

  const events = useMemo<CalendarEvent[]>(() => {
    const map = new Map<string, Pick<CalendarEvent, 'key' | 'date' | 'po' | 'assignments' | 'completed'>>();
    const sliceFor = (po: PurchaseOrderApi, date: string) => {
      const key = `${po.id}__${date}`;
      if (!map.has(key)) map.set(key, { key, date, po, assignments: [], completed: [] });
      return map.get(key)!;
    };
    purchaseOrders.forEach(po => {
      (po.assignments || []).forEach(a => {
        if (a.due_date) sliceFor(po, a.due_date.slice(0, 10)).assignments.push(a);
      });
      // Finalising deletes the assignment, so completed inspections are placed by the due
      // date kept on them — or on the day they were completed when it wasn't kept.
      (po.completed_inspections || []).forEach(ci => {
        const date = ci.due_date ? ci.due_date.slice(0, 10) : toDateKey(new Date(ci.completed_at));
        sliceFor(po, date).completed.push(ci);
      });
    });

    return Array.from(map.values()).map((slice): CalendarEvent => {
      const { assignments, completed } = slice;
      const open = assignments.length > 0;
      const inspectionTypes = [...new Set([
        ...assignments.map(a => a.inspection_type).filter(Boolean),
        ...(completed.length > 0 ? ['final'] : []),
      ])];
      const people = { assignments, completed_inspections: completed };
      return {
        ...slice,
        inspectionTypes,
        assignedTo: getAssignedToPeople(people),
        assignedBy: getAssignedByPeople(people),
        inspection: open
          ? (assignments.some(a => a.started) ? 'In Progress' : 'Scheduled')
          : (completed.some(ci => ci.result === 'fail') ? 'Failed' : 'Passed'),
        status: open ? computeStatus(slice.date) : 'done',
        dueKnown: open || completed.some(ci => ci.due_date),
      };
    });
  }, [purchaseOrders]);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach(ev => {
      if (!map.has(ev.date)) map.set(ev.date, []);
      map.get(ev.date)!.push(ev);
    });
    map.forEach(list => list.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.po.po_number.localeCompare(b.po.po_number)));
    return map;
  }, [events]);

  const selected = selectedKey ? events.find(ev => ev.key === selectedKey) || null : null;
  const closePanel = () => setSelectedKey(null);

  const shiftDate = (amount: number) => {
    setCurrentDate(prev => {
      const nd = new Date(prev);
      if (view === 'month') nd.setMonth(nd.getMonth() + amount);
      else if (view === 'week') nd.setDate(nd.getDate() + amount * 7);
      else nd.setDate(nd.getDate() + amount);
      return nd;
    });
  };

  const periodLabel = useMemo(() => {
    if (view === 'month') return currentDate.toLocaleDateString('default', { month: 'long', year: 'numeric' });
    if (view === 'day') return currentDate.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
    const week = getWeekDates(currentDate);
    const startLabel = week[0].toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    const endLabel = week[6].toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${startLabel} – ${endLabel}`;
  }, [view, currentDate]);

  const monthDates = useMemo(() => getMonthGridDates(currentDate), [currentDate]);
  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);
  const dayEvents = eventsByDate.get(toDateKey(currentDate)) || [];

  return (
    <div className="mx-auto w-full max-w-full">
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex mb-6 text-sm text-slate-500">
        <ol className="inline-flex flex-wrap items-center gap-y-1 space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/dashboard" className="inline-flex items-center hover:text-slate-900 transition-colors">
              <span className="material-symbols-outlined text-[18px] mr-2">dashboard</span>
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">chevron_right</span>
              <Link to="/purchase-orders" className="ml-1 hover:text-slate-900 md:ml-2 transition-colors">Purchase Orders</Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">chevron_right</span>
              <span className="ml-1 font-medium text-[#136bce] md:ml-2">Calendar</span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Purchase Order Calendar</h1>
          <p className="mt-1 text-sm text-slate-500">Assigned and completed inspections, plotted by due date. Click one to see its details.</p>
        </div>
        <button
          onClick={() => navigate('/purchase-orders')}
          className="flex items-center gap-2 self-start rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors md:self-auto"
        >
          <span className="material-symbols-outlined text-[20px]">table_rows</span>
          List View
        </button>
      </div>

      <div className="flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 p-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="inline-flex shrink-0 items-center rounded-lg ring-1 ring-inset ring-slate-300">
              <button onClick={() => shiftDate(-1)} className="rounded-l-lg p-2 text-slate-500 hover:bg-slate-50 transition-colors">
                <span className="material-symbols-outlined text-[20px]">chevron_left</span>
              </button>
              <button onClick={() => setCurrentDate(new Date())} className="border-x border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                Today
              </button>
              <button onClick={() => shiftDate(1)} className="rounded-r-lg p-2 text-slate-500 hover:bg-slate-50 transition-colors">
                <span className="material-symbols-outlined text-[20px]">chevron_right</span>
              </button>
            </div>
            <h2 className="truncate text-lg font-bold text-slate-900">{periodLabel}</h2>
          </div>

          <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-1">
            {(['day', 'week', 'month'] as ViewMode[]).map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`rounded-md px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
                  view === v ? 'bg-white text-[#136bce] shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Legend: chip colour = due date, circle = inspection status */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2.5 text-xs text-slate-500">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            {(['green', 'orange', 'red', 'done'] as DueStatus[]).map(s => (
              <span key={s} className="flex items-center gap-1.5">
                <span className={`h-2.5 w-2.5 rounded-full ${STATUS_STYLES[s].dot}`} />
                {STATUS_LABELS[s]}
              </span>
            ))}
          </div>
          <span className="hidden h-4 w-px bg-slate-200 sm:block" />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            {EVENT_STATUSES.map(s => (
              <span key={s} className="flex items-center gap-1.5">
                <StatusIcon status={s} size="sm" />
                {s}
              </span>
            ))}
          </div>
        </div>

        <div className={`flex ${isWide && selected ? 'min-h-[640px]' : ''}`}>
          {/* Body */}
          <div className="flex min-w-0 flex-1 flex-col">
            {loading ? (
              <div className="flex flex-1 items-center justify-center gap-2 py-24 text-sm text-slate-500">
                <span className="material-symbols-outlined animate-spin text-[20px]">progress_activity</span>
                Loading...
              </div>
            ) : view === 'month' ? (
              <div className="flex flex-1 flex-col">
                <div className="grid grid-cols-7 border-b border-slate-200">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
                    <div
                      key={d}
                      className={`px-1 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-400 sm:px-2 ${i !== 6 ? 'border-r border-slate-100' : ''}`}
                    >
                      {d}
                    </div>
                  ))}
                </div>
                <div className="grid flex-1 grid-cols-7">
                  {monthDates.map((date, idx) => {
                    const key = toDateKey(date);
                    const inMonth = date.getMonth() === currentDate.getMonth();
                    const today = isSameDay(date, new Date());
                    const dateEvents = eventsByDate.get(key) || [];
                    const isLastCol = idx % 7 === 6;
                    return (
                      <div
                        key={key}
                        className={`min-h-[110px] min-w-0 border-b p-1 border-slate-100 sm:p-1.5 ${isLastCol ? '' : 'border-r'} ${inMonth ? 'bg-white' : 'bg-slate-50/50'}`}
                      >
                        <div className="mb-1 flex items-center justify-between">
                          <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                            today ? 'bg-[#136bce] text-white' : inMonth ? 'text-slate-700' : 'text-slate-300'
                          }`}>
                            {date.getDate()}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {dateEvents.slice(0, 3).map(ev => (
                            <EventChip
                              key={ev.key}
                              ev={ev}
                              size="month"
                              selected={ev.key === selectedKey}
                              onClick={() => setSelectedKey(ev.key)}
                            />
                          ))}
                          {dateEvents.length > 3 && (
                            <button
                              onClick={() => { setCurrentDate(date); setView('day'); }}
                              className="text-[10px] font-semibold text-slate-400 hover:text-[#136bce] transition-colors"
                            >
                              +{dateEvents.length - 3}<span className="hidden sm:inline"> more</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : view === 'week' ? (
              <div className="flex flex-1 flex-col">
                <div className="grid grid-cols-7 border-b border-slate-200">
                  {weekDates.map((date, i) => {
                    const today = isSameDay(date, new Date());
                    return (
                      <div
                        key={toDateKey(date)}
                        className={`flex flex-col items-center gap-1 py-3 ${i !== 6 ? 'border-r border-slate-100' : ''}`}
                      >
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                          {date.toLocaleDateString('default', { weekday: 'short' })}
                        </span>
                        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold ${
                          today ? 'bg-[#136bce] text-white' : 'text-slate-900'
                        }`}>
                          {date.getDate()}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="grid min-h-[400px] flex-1 grid-cols-7">
                  {weekDates.map((date, i) => {
                    const key = toDateKey(date);
                    const dateEvents = eventsByDate.get(key) || [];
                    return (
                      <div key={key} className={`min-w-0 space-y-1.5 p-1 sm:p-2 ${i !== 6 ? 'border-r border-slate-100' : ''}`}>
                        {dateEvents.map(ev => (
                          <EventChip
                            key={ev.key}
                            ev={ev}
                            size="week"
                            selected={ev.key === selectedKey}
                            onClick={() => setSelectedKey(ev.key)}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex-1 p-4 sm:p-6">
                {dayEvents.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <span className="material-symbols-outlined text-[40px] mb-2">event_available</span>
                    <p className="text-sm">No purchase orders due on this day.</p>
                  </div>
                ) : (
                  <div className="mx-auto max-w-2xl space-y-3">
                    {dayEvents.map(ev => (
                      <DayEventCard
                        key={ev.key}
                        ev={ev}
                        selected={ev.key === selectedKey}
                        onClick={() => setSelectedKey(ev.key)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Wide screens: the panel docks beside the calendar, scrolling within its height */}
          {isWide && selected && (
            <aside className="animate-panel-in relative w-[360px] shrink-0 border-l border-slate-200 bg-slate-50">
              <div className="absolute inset-0">
                <EventPanel ev={selected} onClose={closePanel} />
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* Smaller screens: the panel slides over the calendar */}
      {!isWide && selected && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[70] print:hidden" role="dialog" aria-modal="true" aria-label={`Purchase order ${selected.po.po_number}`}>
          <div className="animate-backdrop-in absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" onClick={closePanel} />
          <aside className="animate-drawer-in absolute inset-y-0 right-0 flex w-full max-w-[400px] flex-col bg-slate-50 shadow-2xl">
            <EventPanel ev={selected} onClose={closePanel} />
          </aside>
        </div>,
        document.body
      )}
    </div>
  );
};

export default POCalendar;
